import type { FC } from "react";
import React, { useMemo } from "react";

import { getChildrenRecord } from "../../../../../utils/react";
import { UniBoxAdditional } from "../../../../UniBox";
import { INDICATOR_SIZE_BY_AVA_PICTURE_SIZE_MAP } from "../../../constants/sizes";
import { createDisplayNameForAvaPictureElement } from "../../../utils/getDisplayName";
import { createOffsetClassMap } from "../../../utils/getOffsetClassMap";
import { createTestIdForAvaPictureElement } from "../../../utils/getTestId";

import type { AvaPictureShapes, AvaPictureSizes } from "../../../types";

import classes from "./AvaPictureSlotBottomLeft.styl";

type AvaPictureSlotBottomLeftPrivateProps = {
    size: AvaPictureSizes;
    shape: AvaPictureShapes;
    children: React.ReactElement;
};

type AvaPictureSlotBottomLeftProps = Omit<
    AvaPictureSlotBottomLeftPrivateProps,
    "size" | "shape"
>;

const AVA_PICTURE_SLOT_BOTTOM_LEFT_DISPLAY_NAME =
    createDisplayNameForAvaPictureElement("SlotBottomLeft");
const AVA_PICTURE_SLOT_BOTTOM_LEFT_TEST_ID =
    createTestIdForAvaPictureElement("SlotBottomLeft");

const INDICATOR_OFFSET_CLASS_BY_SIZE_MAPS = {
    circle: createOffsetClassMap("indicator-online-offsets-circle", classes),
    squircle: createOffsetClassMap(
        "indicator-online-offsets-squircle",
        classes,
    ),
};

export const AvaPictureSlotBottomLeft: FC<AvaPictureSlotBottomLeftProps> = (
    props,
) => {
    const { size, shape, children } =
        props as AvaPictureSlotBottomLeftPrivateProps;

    const { OnlineIndicatorWeb, OnlineIndicatorMob } = useMemo(
        () =>
            getChildrenRecord(
                children,
                AVA_PICTURE_SLOT_BOTTOM_LEFT_DISPLAY_NAME,
                {
                    OnlineIndicatorWeb: {
                        props: {
                            size: INDICATOR_SIZE_BY_AVA_PICTURE_SIZE_MAP[size],
                        },
                        optionalRequired: true,
                    },
                    OnlineIndicatorMob: {
                        props: {
                            size: INDICATOR_SIZE_BY_AVA_PICTURE_SIZE_MAP[size],
                        },
                        optionalRequired: true,
                    },
                    Unknown: {
                        skipElement: true,
                    },
                },
            ),
        [children, size],
    );

    const shouldShowChild = ["16", "20"].indexOf(size) === -1;

    return (
        <UniBoxAdditional.SlotBottomLeft
            data-tsid={AVA_PICTURE_SLOT_BOTTOM_LEFT_TEST_ID}
            customVarsStyles={INDICATOR_OFFSET_CLASS_BY_SIZE_MAPS[shape][size]}
        >
            {shouldShowChild && (OnlineIndicatorWeb || OnlineIndicatorMob)}
        </UniBoxAdditional.SlotBottomLeft>
    );
};

AvaPictureSlotBottomLeft.displayName =
    AVA_PICTURE_SLOT_BOTTOM_LEFT_DISPLAY_NAME;

export default AvaPictureSlotBottomLeft;
